/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomSearchbarWithApiV2.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import React, { useEffect, useRef, useState } from "react";
import {
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../../../redux/actions";
import CloseIcon from "@mui/icons-material/Close";
import { useAsyncError } from "react-router-dom";

const CustomSearchbarWithApiV2 = ({
  apiAction,
  params = {},
  skipApiCall,
  searchKey,
  getDataOnSearch,
  triggerApi,
  onSearchQueryChange,
  size,
  type,
  variant,
  placeholder,
  valueError,
  isMultiline,
  minRows,
  maxRows,
  disabled,
  txtFldStyle = {},
  iconColor = "black",
  setSearchFieldValue,
  skipInitialSearch = true, // NEW: Skip initial API call on mount
}) => {
  const dispatch = useDispatch();
  const isFirstRun = useRef(true);
  const prevParamsRef = useRef(params);
  const didMountRef = useRef(false);
  const searchTextRef = useRef("");
  const [prevParams, setPrevParams] = useState(params);
  const [searchText, setSearchText] = useState("");
  const [prevSearch, setPrevSearch] = useState("");
  // const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  console.log(params, "paramsparamsparams");

  // Reset searchText on component mount
  useEffect(() => {
    setSearchText("");
  }, []);

  useEffect(() => {
    searchTextRef.current = searchText;
  }, [searchText]);

  const handleSearch = async (query) => {
    console.log("---handleSearch---", query);
    if (onSearchQueryChange) {
      onSearchQueryChange(query, prevSearch);
    }

    if (skipApiCall) {
      getDataOnSearch([], { error: false, data: [] });
      return;
    }

    setLoading(true);

    setPrevSearch(query);
    // Pass search query to parent component if callback provided

    try {
      console.log(skipApiCall);
      const response = await dispatch(
        HandleApiActions({
          ...apiAction,
          params: {
            ...params,
            ...(query !== "" && { [searchKey]: query }),
          },
          show_toast: false,
          ignore_app_loading_spinner: true,
        }),
      );
      console.log("search response", response);

      if (!response?.error) {
        getDataOnSearch(response.data, response);
      } else {
        getDataOnSearch([], response);
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
      getDataOnSearch([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const delayDebounce = setTimeout(() => {
      handleSearch(searchText);
    }, 500);

    return () => clearTimeout(delayDebounce); // cleanup on next effect
  }, [searchText]);

  // Trigger search when params change
  useEffect(() => {
    const paramsChanged =
      JSON.stringify(prevParamsRef.current) !== JSON.stringify(params);

    if (isFirstRun.current) {
      // Only call API on first run if skipInitialSearch is false
      if (!skipInitialSearch) {
        handleSearch(searchTextRef.current);
      }
      isFirstRun.current = false;
    } else if (paramsChanged) {
      // Call API when params actually change
      handleSearch(searchTextRef.current);
    }

    prevParamsRef.current = params; // update reference without rerender
  }, [params, skipInitialSearch]);

  useEffect(() => {
    if (!isFirstRun.current) {
      handleSearch(searchText);
    }
  }, [triggerApi]);

  const clearSearch = () => {
    if (onSearchQueryChange) {
      onSearchQueryChange("", searchText);
    }
    setSearchText("");
    handleSearch("");
  };

  //function to handel change value
  const handleSearchValue = (e) => {
    setSearchText(e.target.value);
  };

  return (
    <TextField
      fullWidth
      size={size}
      type={type}
      variant={variant}
      placeholder={placeholder}
      value={searchText}
      error={Boolean(valueError)}
      helperText={valueError}
      multiline={isMultiline}
      minRows={minRows}
      maxRows={maxRows}
      disabled={disabled}
      onChange={handleSearchValue}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleSearch(searchText);
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: iconColor }} />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment
            position="end"
            sx={{
              width: 32, // 🔒 FIXED WIDTH
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: iconColor }} />
            ) : (
              searchText && (
                <IconButton onClick={clearSearch} size="small">
                  <CloseIcon sx={{ color: iconColor }} />
                </IconButton>
              )
            )}
          </InputAdornment>
        ),
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          height: "28px",
          "& fieldset": { borderColor: "#D0D0CE", borderRadius: "0px" }, // Default
          "&:hover fieldset": { borderColor: "#D0D0CE" }, // Hover
          "&.Mui-focused fieldset": { borderColor: "#000000" }, // Focused
          "&.Mui-disabled fieldset": { borderColor: "#D0D0CE" }, // Disabled
          "&.Mui-error fieldset": { borderColor: "#e53935" }, // Error
        },
        // 🔹 Change text color on hover
        /* Hover placeholder */
        "& .MuiOutlinedInput-root:hover .MuiInputBase-input::placeholder": {
          color: "#848587",
        },

        "& .MuiOutlinedInput-root.Mui-focused .MuiInputBase-input::placeholder":
        {
          color: "#232323",
          opacity: 1,
        },

        "& .MuiInputLabel-root": {
          color: "#000",
          fontSize: "0.9rem",
          "&.Mui-error": { color: "#e53935" },
          "&.Mui-disabled": { color: "#bdbdbd" },
        },

        "& .MuiInputBase-input::placeholder": {
          p: 0,
          m: 0,
          color: "#999",
          opacity: 1,
        },
        "& .MuiFormHelperText-root.Mui-error": {
          color: "#e53935",
          fontSize: "0.8rem",
          marginLeft: 0,
          marginTop: "4px",
        },
        ...txtFldStyle,
      }}
    />
  );
};

export default CustomSearchbarWithApiV2;
